import { api } from "@/lib/api";

const base = "/api/reports";

export interface DailyCensus {
  date: string;
  opdVisits: number;
  ipdAdmissionsToday: number;
  ipdDischargesToday: number;
  currentInPatients: number;
}

export interface DoctorProductivityPatientItem {
  patientId: string;
  patientName: string;
  uhid: string | null;
  opdRevenue: number;
  ipdRevenue: number;
  labRevenue: number;
  pharmaRevenue: number;
  daycareRevenue: number;
  totalRevenue: number;
  /** Earliest/latest payment (or IPD discharge) date for each bucket. */
  opdDateFrom?: string | null;
  opdDateTo?: string | null;
  ipdDateFrom?: string | null;
  ipdDateTo?: string | null;
  labDateFrom?: string | null;
  labDateTo?: string | null;
  pharmaDateFrom?: string | null;
  pharmaDateTo?: string | null;
  daycareDateFrom?: string | null;
  daycareDateTo?: string | null;
}

export interface DoctorProductivityItem {
  doctorId: string;
  name: string;
  specialisation: string | null;
  appointments: number;
  opdRevenue: number;
  ipdRevenue: number;
  labRevenue: number;
  pharmaRevenue: number;
  daycareRevenue: number;
  totalRevenue: number;
  /** @deprecated Use totalRevenue */
  revenue?: number;
}

export interface BedOccupancyWard {
  wardId: string;
  wardName: string;
  type: string;
  totalBeds: number;
  occupied: number;
  available: number;
  occupancyPercent: number;
}

export interface BedOccupancy {
  date: string;
  wards: BedOccupancyWard[];
  summary: {
    totalBeds: number;
    totalOccupied: number;
    totalAvailable: number;
    occupancyPercent: number;
  };
}

export interface FunnelData {
  leadsNew: number;
  leadsConverted: number;
  leadsLost: number;
  newPatients: number;
  opdVisits: number;
  opdToIpd: number;
  ipdAdmissions: number;
}

export interface CustomerVoiceSectionAvg {
  key: string;
  label: string;
  average: number | null;
  count: number;
  min?: number;
  max?: number;
}

export interface CustomerVoiceReport {
  generatedAt: string;
  hospitalId: string;
  range: { from: string | null; to: string | null };
  opd: {
    source: string;
    responseCount: number;
    sections: CustomerVoiceSectionAvg[];
    recentComments: Array<{
      receivedAt: string;
      patientName: string | null;
      phone: string;
      rating: number | null;
      comment: string | null;
    }>;
  };
  ipd: {
    source: string;
    sheetId: string;
    gid: string;
    responseCount: number;
    sections: CustomerVoiceSectionAvg[];
    npsAverage: number | null;
    npsCount: number;
    npsLabel: string;
    recentComments: Array<{
      receivedAt: string;
      patientName: string | null;
      patientId: string | null;
      phone: string | null;
      overall: number | null;
      nps: number | null;
      comment: string | null;
      standoutStaff: string | null;
    }>;
  };
  notes: string[];
}

type ReportsParams = { hospitalId?: string | null };

export const reportsApi = {
  getDailyCensus: (date?: string, params?: ReportsParams) =>
    api.get<{ data: DailyCensus }>(`${base}/daily-census`, {
      params: { ...(date ? { date } : {}), ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
  getDoctorProductivity: (from?: string, to?: string, params?: ReportsParams) =>
    api.get<{ data: { from: string; to: string; doctors: DoctorProductivityItem[] } }>(`${base}/doctor-productivity`, {
      params: { from, to, ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
  getDoctorProductivityPatients: (doctorId: string, from?: string, to?: string, params?: ReportsParams) =>
    api.get<{
      data: {
        from: string;
        to: string;
        doctor: { id: string; name: string; specialisation: string | null };
        patients: DoctorProductivityPatientItem[];
      };
    }>(`${base}/doctor-productivity/${doctorId}/patients`, {
      params: { from, to, ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
  getBedOccupancy: (date?: string, params?: ReportsParams) =>
    api.get<{ data: BedOccupancy }>(`${base}/bed-occupancy`, {
      params: { ...(date ? { date } : {}), ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
  getFunnel: (from?: string, to?: string, params?: ReportsParams) =>
    api.get<{ data: { from: string; to: string; funnel: FunnelData } }>(`${base}/funnel`, {
      params: { from, to, ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
  getCustomerVoice: (from?: string, to?: string, params?: ReportsParams) =>
    api.get<{ data: CustomerVoiceReport }>(`${base}/customer-voice`, {
      params: { from, to, ...(params?.hospitalId ? { hospitalId: params.hospitalId } : {}) },
    }),
};
